import { machineTestReportsApi } from "@/api/machineTestReportsApi";
import * as machineTestReportsHelper from "../../helpers/machineTestReportsHelper";
import { roundValue } from "@/components/analytics/helpers";

const configUdmVars = {
  Accel: "mm/s²",
  Speed: "mm/s",
  Temperature: "°C",
  Time: "ms"
};

export default {
  state: {
    loading: true,
    lastCycle: {},
    lastCyclesWithTool: {},
    subCyclesByCycle: {},
    subCyclesSeries: {},
    fingerprint: {}
  },
  getters: {
    isLoading: state => {
      return state.loading;
    },
    getLastCycleByMachineId: state => (machineId, type) => {
      return machineId && type && state.lastCycle[machineId] && state.lastCycle[machineId][type]
        ? state.lastCycle[machineId][type]
        : null;
    },
    getLastCyclesWithToolByMachineId: state => machineId => {
      return machineId && state.lastCyclesWithTool[machineId]
        ? state.lastCyclesWithTool[machineId]
        : null;
    },
    getSubCyclesByCycle: state => cycleId => {
      return state.subCyclesByCycle[cycleId] || null;
    },
    getSubCycleSerie: state => subCycleId => {
      return state.subCyclesSeries[subCycleId] || null;
    },
    getFingerprintCycleByMachineId: state => (machineId, type) => {
      return machineId && type && state.fingerprint[machineId] && state.fingerprint[machineId][type]
        ? state.fingerprint[machineId][type]
        : null;
    }
  },
  actions: {
    async fetchCollisions(
      // eslint-disable-next-line no-empty-pattern
      {},
      { machineId, skip, limit, timeFrom, timeTo }
    ) {
      if (machineId) {
        try {
          const response = await machineTestReportsApi.getCollisions(
            machineId,
            skip,
            limit,
            timeFrom,
            timeTo
          );
          return response;
        } catch (e) {
          console.error(`[Error] Fetch collisions by machine ${machineId} failed.`, e);
        }
      }
    },

    async fetchCollisionsChart(
      // eslint-disable-next-line no-empty-pattern
      {},
      { machineId, timeFrom, timeTo }
    ) {
      if (machineId) {
        try {
          const response = await machineTestReportsApi.getCollisions(
            machineId,
            null,
            0,
            timeFrom,
            timeTo
          );

          const results = response.result;
          return results;
        } catch (e) {
          console.error(
            `[Error] Fetch all filtered time collisions by machine ${machineId} failed.`,
            e
          );
        }
      }
    },

    async fetchLastCycle({ commit, getters }, { machineId, type }) {
      if (machineId && !getters["getLastCycleByMachineId"](machineId, type)) {
        commit("SET_LOADING", true);

        try {
          const res = await machineTestReportsApi.getLastCycle(machineId, type);

          let data = res && res.length > 0 ? res[0] : null;

          // Parse JSON string for RESULT obj
          if (data) {
            data = {
              ...data,
              result: machineTestReportsHelper.checkJsonString(data.result)
            };
            commit("UPDATE_LAST_CYCLE", {
              machineId,
              payload: { data },
              type
            });
          }
        } catch (e) {
          console.error("[Error] Fetch last cycle by machine failed.", e);
        }

        commit("SET_LOADING", false);
      }
    },

    async fetchLastCyclesWithTool({ commit, getters }, { machineId }) {
      if (machineId && !getters["getLastCyclesWithToolByMachineId"](machineId)) {
        commit("SET_LOADING", true);

        try {
          const res = await machineTestReportsApi.getLastCyclesWithTool(machineId);

          // Parse JSON string for RESULT obj
          if (res && res.length > 0) {
            const data = res.map(cycle => ({
              ...cycle,
              result: machineTestReportsHelper.checkJsonString(cycle.result)
            }));

            commit("UPDATE_LAST_CYCLES_WITH_TOOL", {
              machineId,
              payload: { data }
            });
          }
        } catch (e) {
          console.error("[Error] Fetch last cycles with tool by machine failed.", e);
        }

        commit("SET_LOADING", false);
      }
    },

    async fetchCycles(
      // eslint-disable-next-line no-empty-pattern
      {},
      { machineId, skip, limit, isFingerprint, type, startTimeFrom, startTimeTo }
    ) {
      if (machineId && type) {
        try {
          let response = await machineTestReportsApi.getCycles(
            machineId,
            skip,
            limit,
            isFingerprint,
            startTimeFrom,
            startTimeTo,
            type
          );

          // Parse JSON string for RESULT obj
          const result = response.result.map(item => {
            return {
              ...item,
              result: machineTestReportsHelper.checkJsonString(item.result)
            };
          });

          response = {
            ...response,
            result
          };

          return response;
        } catch (e) {
          console.error("[Error] Fetch cycles by machine failed.", e);
        }
      }
    },

    async fetchCyclesCharts(
      // eslint-disable-next-line no-empty-pattern
      {},
      { machineId, startTimeFrom, startTimeTo, isFingerprint, type }
    ) {
      if (machineId && type) {
        try {
          const response = await machineTestReportsApi.getCycles(
            machineId,
            null,
            0,
            isFingerprint,
            startTimeFrom,
            startTimeTo,
            type
          );

          // Parse JSON string for RESULT obj
          const cycles = response.result.map(item => {
            const cycleResult = machineTestReportsHelper.checkJsonString(item.result);

            let score = undefined;
            let status = undefined;
            if (cycleResult && cycleResult.result) {
              status = cycleResult.result.status;
              score = cycleResult.result.score;
            }

            return {
              ...item,
              machineId,
              status,
              score
            };
          });

          return cycles;
        } catch (e) {
          console.error("[Error] Fetch cycles by machine failed.", e);
        }
      }
    },
    async fetchSubCyclesByCycle({ commit, getters }, { cycleId }) {
      if (cycleId) {
        commit("SET_LOADING", true);

        try {
          const subCycles = getters.getSubCyclesByCycle(cycleId);

          if (subCycles) {
            commit("SET_LOADING", false);
            return subCycles;
          }

          const payload = await machineTestReportsApi.getSubCycles(cycleId);

          commit("UPDATE_SUB_CYCLES", {
            cycleId,
            payload
          });

          commit("SET_LOADING", false);
          return payload;
        } catch (e) {
          commit("SET_LOADING", false);
          console.error("[Error] Fetch check sub cycles by machine failed.", e);
        }
      }
    },
    async fetchSubCycleData(
      { commit, getters },
      { machineId, applicationId, cycleId, subCycleId, appName }
    ) {
      if (machineId && subCycleId && !getters["getSubCycleSerie"](subCycleId)) {
        commit("SET_LOADING", true);

        const subcycles = getters["getSubCyclesByCycle"](cycleId);

        if (subcycles) {
          const filename = subcycles.find(item => item.uuid === subCycleId).timeSerie;

          try {
            const res = await machineTestReportsApi.getSubCycleData(
              machineId,
              applicationId,
              filename
            );

            const updateByApplication = `UPDATE_SUB_CYCLES_SERIES_${appName.toUpperCase()}`;

            commit(updateByApplication, {
              subCycleId,
              payload: res
            });
          } catch (e) {
            console.error("[Error] Fetch sub cycles data by machine failed.", e);
          }
        }

        commit("SET_LOADING", false);
        return true;
      }
    },
    async fetchFingerprintCycle({ commit, getters }, { machineId, type }) {
      if (machineId && !getters["getFingerprintCycleByMachineId"](machineId, type)) {
        commit("SET_LOADING", true);

        try {
          const result = await machineTestReportsApi.getLastFingerprint(machineId, type);
          let data = result && result.length > 0 ? result[0] : null;

          // Parse JSON string for RESULT obj
          data = {
            ...data,
            result: machineTestReportsHelper.checkJsonString(data.result)
          };

          commit("UPDATE_FINGERPRINT", {
            machineId,
            payload: { data },
            type
          });
        } catch (e) {
          console.error(
            `[Error] Fetch ${type} fingerprint cycle by machineId ${machineId} failed.`,
            e
          );
        }

        commit("SET_LOADING", false);
      }
    }
  },
  mutations: {
    SET_LOADING: (state, loading) => {
      state.loading = loading;
    },
    UPDATE_LAST_CYCLE: (state, { machineId, payload, type }) => {
      state.lastCycle = {
        ...state.lastCycle,
        [machineId]: {
          ...state.lastCycle[machineId],
          [type]: payload.data
        }
      };
    },
    UPDATE_LAST_CYCLES_WITH_TOOL: (state, { machineId, payload }) => {
      state.lastCyclesWithTool = {
        ...state.lastCyclesWithTool,
        [machineId]: payload.data
      };
    },
    UPDATE_SUB_CYCLES: (state, { cycleId, payload }) => {
      state.subCyclesByCycle = {
        ...state.subCyclesByCycle,
        [cycleId]: payload
      };
    },
    UPDATE_SUB_CYCLES_SERIES_AXESCHECK: (state, { subCycleId, payload }) => {
      const values = {
        data: payload.data.data,
        config: payload.data.config,
        optData: payload.data.optData
      };
      const yAxisUnit = [];
      const categories = values.data?.t || [];
      const axisNames = [];

      const mapSeries = values.config.map(item => {
        const axisName = values.optData[item.axisId].name;
        if (axisNames.indexOf(axisName) < 0) {
          axisNames.push(values.optData[item.axisId].name);
        }
        return {
          ...machineTestReportsHelper.configAxisType[item.axTypeToRead],
          axisName
        };
      });

      // Map series
      const series = Object.values(mapSeries).map((item, i) => {
        const { unit, name, color, axisName } = item;
        /*
        // Data [ [1, 4353], [1, 0.45646], ... ]
        const data = values.data?.v[i].map((val, index) => {
          return [index, machineTestReportsHelper.roundValue(val, 5)];
        });
        */
        // Data [ 4353, 0.45646, ... ] array simple
        const data = values.data?.v[i];

        const y_axis_index = yAxisUnit.findIndex(el => el.title.text === name);

        if (y_axis_index < 0) {
          yAxisUnit.push({
            unit,
            title: {
              text: item.name
            },
            //softMin,
            //softMax,
            lineColor: color
          });
        }

        return {
          name,
          unit,
          color,
          data,
          yAxis: y_axis_index > -1 ? y_axis_index : i, //yAxisUnit.length - 1 //y_index
          custom: {
            axisName
          }
        };
      });

      state.subCyclesSeries = {
        ...state.subCyclesSeries,
        [subCycleId]: {
          categories,
          series,
          yAxis: yAxisUnit,
          axisNames,
          originals: values
        }
      };
    },

    // eslint-disable-next-line no-unused-vars
    UPDATE_SUB_CYCLES_SERIES_DEVICESCHECK: (state, { subCycleId, payload }) => {
      const devicesConfig = payload.data.config?.devices;
      const devicesData = payload.data.data?.devices;

      const y_axis = [
        {
          title: {
            text: configUdmVars.Time
          }
        }
      ];

      const timeVarsSeriesName = {
        reactionTime1: {
          udm: configUdmVars.Time,
          label: "reaction_time_1",
          color: "#FFA833"
        },
        reactionTime2: {
          udm: configUdmVars.Time,
          label: "reaction_time_2",
          color: "#b30000"
        },
        posTime1: {
          udm: configUdmVars.Time,
          label: "pos_time_1",
          color: "#1F6D45"
        },
        posTime2: {
          udm: configUdmVars.Time,
          label: "pos_time_2",
          color: "#192DF3"
        }
      };

      const chartMode = {
        ADVANCED: {
          loadSeries: ["reactionTime1", "reactionTime2", "posTime1", "posTime2"],
          series: [],
          yAxis: y_axis,
          devicesConfig
        }
      };

      const categories = devicesConfig.map(item => item.name);

      const timeVarsSeries = Object.keys(timeVarsSeriesName).map(name => {
        const data = devicesData.map(item => ({
          y: item[name]
        }));

        const { udm, label, color } = timeVarsSeriesName[name];

        return {
          name: label,
          data,
          yAxis: y_axis.findIndex(axis => axis.title.text === udm), // Text yAxis = "ms" like udm var
          color,
          custom: {
            extraInformation: udm
          }
        };
      });

      chartMode.ADVANCED.series = timeVarsSeries;
      chartMode.ADVANCED.categories = categories;

      state.subCyclesSeries = {
        ...state.subCyclesSeries,
        [subCycleId]: {
          ...chartMode,
          originals: payload
        }
      };
    },
    UPDATE_SUB_CYCLES_SERIES_TOOLSCHECK: (state, { subCycleId, payload }) => {
      const timeVarsData = payload.data.data.timeVars.data;
      const extra = {
        tool: payload.data.config.tool
      };

      const y_axis_time_vars = [
        {
          title: {
            text: "Accel"
          },
          lineColor: "#002680"
        },
        {
          title: {
            text: "Speed"
          },
          lineColor: "#026cff"
        },
        {
          title: {
            text: "Other"
          },
          lineColor: "#ac41d6"
        }
      ];
      const x_axis_time_vars = {
        title: {
          text: "TS"
        }
      };

      const timeVarsSeriesName = {
        AccPeakVect: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel"
        },
        AccValX: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel X axis"
        },
        AccValY: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel Y axis"
        },
        AccValZ: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel Z axis"
        },
        VelRmsVect: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed"
        },
        VelRmsX: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed X axis"
        },
        VelRmsY: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed Y axis"
        },
        VelRmsZ: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed Z axis"
        },
        Temperature: {
          yAxisName: "Other",
          udm: configUdmVars.Temperature,
          textTitle: "Temperature"
        }
      };

      const subcycleChartModes = {
        SIMPLE: {
          loadSeries: ["AccPeakVect", "VelRmsVect"],
          series: [],
          yAxis: [{ ...y_axis_time_vars[0] }, { ...y_axis_time_vars[1] }],
          extra
        },
        ADVANCED: {
          loadSeries: [
            "AccPeakVect",
            "AccValX",
            "AccValY",
            "AccValZ",
            "VelRmsVect",
            "VelRmsX",
            "VelRmsY",
            "VelRmsZ",
            "Temperature"
          ],
          series: [],
          yAxis: y_axis_time_vars,
          xAxis: x_axis_time_vars,
          extra
        }
      };

      const timeVarsSeries = Object.keys(timeVarsSeriesName).map(name => {
        const data = timeVarsData.map(item => ({ x: item.TS, y: item[name] }));
        const { yAxisName, udm, textTitle } = timeVarsSeriesName[name];

        return {
          name,
          data,
          yAxis: y_axis_time_vars.findIndex(axis => axis.title.text === yAxisName),
          custom: {
            extraInformation: udm,
            name: textTitle
          }
        };
      });

      subcycleChartModes.SIMPLE.series = subcycleChartModes.SIMPLE.loadSeries.map(loadSerie => {
        return timeVarsSeries.find(serie => serie.name === loadSerie);
      });

      subcycleChartModes.ADVANCED.series = timeVarsSeries;

      state.subCyclesSeries = {
        ...state.subCyclesSeries,
        [subCycleId]: {
          ...subcycleChartModes,
          originals: payload
        }
      };
    },

    UPDATE_SUB_CYCLES_SERIES_SPINDLECHECK: (state, { subCycleId, payload, extra = null }) => {
      const timeVarsData = payload.data.data.timeVars.data;
      const frequencyVarsData = payload.data.data.frequencyVars.data;

      const axisNames = [];
      axisNames.push(payload.data.data.frequencyVars.axes);

      const y_axis_time_vars = [
        {
          title: {
            text: "Accel"
          },
          lineColor: "#002680"
        },
        {
          title: {
            text: "Speed"
          },
          lineColor: "#026cff"
        },
        {
          title: {
            text: "Other"
          },
          lineColor: "#ac41d6"
        }
      ];
      const x_axis_time_vars = {
        title: {
          text: "TS"
        }
      };

      const y_axis_frequency = [
        {
          title: {
            text: "Values"
          }
        }
      ];

      const x_axis_frequency = {
        title: {
          text: "Hz"
        }
      };

      const timeVarsSeriesName = {
        AccPeakVect: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel"
        },
        AccValX: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel X axis"
        },
        AccValY: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel Y axis"
        },
        AccValZ: {
          yAxisName: "Accel",
          udm: configUdmVars.Accel,
          textTitle: "Accel Z axis"
        },
        VelRmsVect: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed"
        },
        VelRmsX: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed X axis"
        },
        VelRmsY: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed Y axis"
        },
        VelRmsZ: {
          yAxisName: "Speed",
          udm: configUdmVars.Speed,
          textTitle: "Speed Z axis"
        },
        Temperature: {
          yAxisName: "Other",
          udm: configUdmVars.Temperature,
          textTitle: "Temperature"
        }
      };

      const frequencyVarsSeriesName = {
        FrequencyValue: {
          keyDataRawName: "value",
          textTitle: "Val",
          chartType: "column"
        }
      };

      const spindlecheckSubcycleChartModes = {
        SIMPLE: {
          loadSeries: ["AccPeakVect", "VelRmsVect"],
          series: [],
          yAxis: [{ ...y_axis_time_vars[0] }, { ...y_axis_time_vars[1] }],
          axisNames,
          extra
        },
        ADVANCED: {
          loadSeries: [
            "AccPeakVect",
            "AccValX",
            "AccValY",
            "AccValZ",
            "VelRmsVect",
            "VelRmsX",
            "VelRmsY",
            "VelRmsZ",
            "Temperature"
          ],
          series: [],
          yAxis: y_axis_time_vars,
          xAxis: x_axis_time_vars,
          axisNames,
          extra
        },
        FREQUENCY: {
          series: [],
          yAxis: y_axis_frequency,
          xAxis: x_axis_frequency,
          axisNames,
          extra
        }
      };

      const timeVarsSeries = Object.keys(timeVarsSeriesName).map(name => {
        const data = timeVarsData.map(item => ({ x: item.TS, y: item[name] }));
        const { yAxisName, udm, textTitle } = timeVarsSeriesName[name];

        return {
          name,
          data,
          yAxis: y_axis_time_vars.findIndex(axis => axis.title.text === yAxisName),
          custom: {
            extraInformation: udm,
            name: textTitle
          }
        };
      });

      spindlecheckSubcycleChartModes.SIMPLE.series =
        spindlecheckSubcycleChartModes.SIMPLE.loadSeries.map(loadSerie => {
          return timeVarsSeries.find(serie => serie.name === loadSerie);
        });

      spindlecheckSubcycleChartModes.ADVANCED.series = timeVarsSeries;

      spindlecheckSubcycleChartModes.FREQUENCY.series = Object.keys(frequencyVarsSeriesName).map(
        name => {
          const { textTitle, chartType } = frequencyVarsSeriesName[name];
          return {
            name,
            data: frequencyVarsData.map(item => ({
              x: item.centerBand,
              y: roundValue(item[frequencyVarsSeriesName[name]["keyDataRawName"].toLowerCase()], 5)
            })),
            yAxis: 0,
            custom: {
              name: textTitle
            },
            type: chartType
          };
        }
      );

      state.subCyclesSeries = {
        ...state.subCyclesSeries,
        [subCycleId]: {
          ...spindlecheckSubcycleChartModes,
          originals: payload
        }
      };
    },
    UPDATE_FINGERPRINT: (state, { machineId, payload, type }) => {
      state.fingerprint = {
        ...state.fingerprint,
        [machineId]: {
          ...state.fingerprint[machineId],
          [type]: payload.data
        }
      };
    },
    RESET: state => {
      state.loading = false;
      //state.cycles = {};
      state.fingerprint = {};
      state.subCyclesByCycle = {};
      state.subCyclesSeries = {};
      state.lastCycle = {};
      state.lastCyclesWithTool = {};
    }
  },
  namespaced: true,
  modules: {}
};
